/**
 * Imágenes de las plantillas (`/activa/oficial/imagenes`, fase 2): el lugar donde se suben las fotos de las
 * promociones. Cada una queda con un link https que WhatsApp puede abrir, y el editor de plantillas lo pone solo en el
 * encabezado (pedido de Alan del 19/10/2026: «un lugar de medios que genere una URL y que se ponga en automático»).
 *
 * - Subir y borrar son del equipo; una persona de un cliente sólo ve la lista (como ve las plantillas).
 * - Una imagen que usa una plantilla sin archivar no se borra: el backend lo frena y acá se dice cuál la usa.
 * - Borrar pide confirmación en la misma tarjeta, no en una ventana.
 */

import { useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { AlertTriangle, Check, Copy, ImagePlus, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { useImagenesOficial } from "../hooks/useImagenesOficial";
import { ACEPTA_IMAGEN, avisoDeMedidas, medidasLegibles, pesoLegible } from "../logic/imagenes";
import type { ImagenOficial } from "../types";

export default function ImagenesPage() {
  const equipo = useEsEquipoDeenex();
  const { lista, cargando, error, subiendo, subir, errorSubida, borrar, recargar } = useImagenesOficial();
  const entrada = useRef<HTMLInputElement>(null);
  const [aviso, setAviso] = useState("");

  const alElegir = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setAviso("");
    const r = await subir(f);
    if (r?.yaEstaba) setAviso(`Esa imagen ya estaba subida como «${r.imagen.nombre}»: es la misma, con el mismo link.`);
  };

  if (cargando && !lista) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Cargando las imágenes">
        {[0, 1].map((i) => <div key={i} className="h-36 rounded-2xl bg-slate-100 animate-pulse" />)}
      </div>
    );
  }
  if (!lista) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <p className="text-sm font-semibold text-red-700">No pudimos traer las imágenes.</p>
        <p className="text-sm text-red-700/80 mt-1">{error}</p>
        <button type="button" onClick={() => void recargar()} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-red-800 hover:underline">
          <RefreshCw size={14} /> Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">Imágenes</h2>
          <p className="text-sm text-slate-500 mt-1">
            Las fotos de tus plantillas. Cada una tiene un link que WhatsApp puede abrir, y el editor de plantillas lo pone solo.
          </p>
        </div>
        {equipo && (
          <>
            <input ref={entrada} type="file" accept={ACEPTA_IMAGEN} className="hidden" aria-label="Elegir una imagen para subir" onChange={(e) => void alElegir(e)} />
            <button type="button" onClick={() => entrada.current?.click()} disabled={subiendo}
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
              {subiendo ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
              {subiendo ? "Subiendo…" : "Subir una imagen"}
            </button>
          </>
        )}
      </div>

      {!lista.conLink && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="alert">
          <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
          <span>El servidor no tiene su dirección pública (PUBLIC_BASE_URL): las imágenes se guardan, pero sin un link que WhatsApp pueda abrir.</span>
        </div>
      )}
      {errorSubida && (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /> <span>{errorSubida}</span>
        </div>
      )}
      {aviso && <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" role="status">{aviso}</p>}
      {error && <p className="text-sm text-amber-700" role="alert">{error}</p>}

      {lista.imagenes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center">
          <p className="text-sm font-semibold text-slate-700">Todavía no hay imágenes.</p>
          <p className="text-sm text-slate-500 mt-1">
            {equipo ? `Subí la primera: JPG o PNG de hasta ${pesoLegible(lista.maxBytes)}.` : "Las sube el equipo de Deenex."}
          </p>
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-label="Imágenes de la marca">
          {lista.imagenes.map((i) => <TarjetaImagen key={i.id} imagen={i} equipo={equipo} borrar={borrar} />)}
        </ul>
      )}
    </div>
  );
}

function TarjetaImagen({ imagen, equipo, borrar }: { imagen: ImagenOficial; equipo: boolean; borrar: (id: string) => Promise<string | null> }) {
  const [confirmando, setConfirmando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [problema, setProblema] = useState("");
  const [copiado, setCopiado] = useState(false);
  const medidas = medidasLegibles(imagen);
  const avisoMedidas = avisoDeMedidas(imagen);

  const copiar = async () => {
    if (!imagen.url) return;
    try {
      await navigator.clipboard.writeText(imagen.url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setProblema("No se pudo copiar: seleccioná el link y copialo a mano.");
    }
  };
  const confirmarBorrado = async () => {
    setBorrando(true);
    setProblema("");
    const p = await borrar(imagen.id);
    // si salió, la tarjeta desaparece de la lista; si no, queda con el motivo
    setBorrando(false);
    if (p) {
      setProblema(p);
      setConfirmando(false);
    }
  };

  return (
    <li className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden flex flex-col">
      <div className="aspect-[1.91/1] bg-slate-50 flex items-center justify-center overflow-hidden">
        {imagen.url ? (
          <img src={imagen.url} alt={imagen.nombre} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <span className="text-xs text-slate-400">Sin link</span>
        )}
      </div>
      <div className="p-4 space-y-2 flex-1 flex flex-col">
        <p className="text-sm font-semibold text-slate-900 truncate" title={imagen.nombre}>{imagen.nombre}</p>
        <p className="text-xs text-slate-500">{[pesoLegible(imagen.bytes), medidas, imagen.tipo === "image/png" ? "PNG" : "JPG"].filter(Boolean).join(" · ")}</p>
        {avisoMedidas && <p className="text-xs text-amber-700">{avisoMedidas}</p>}
        <p className="text-xs text-slate-500">
          {imagen.usos.length
            ? `La usa ${imagen.usos.length === 1 ? "la plantilla" : "las plantillas"} ${imagen.usos.map((u) => `«${u.nombreVisible}»`).join(", ")}.`
            : "Ninguna plantilla la usa todavía."}
        </p>
        {imagen.url && (
          <input readOnly value={imagen.url} aria-label={`Link de ${imagen.nombre}`} onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-mono text-slate-600" />
        )}
        {problema && <p className="text-xs text-red-700" role="alert">{problema}</p>}
        <div className="mt-auto pt-2 flex items-center gap-3 flex-wrap">
          {imagen.url && (
            <button type="button" onClick={() => void copiar()} className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 hover:underline">
              {copiado ? <Check size={13} /> : <Copy size={13} />} {copiado ? "Copiado" : "Copiar link"}
            </button>
          )}
          {equipo && !confirmando && (
            <button type="button" onClick={() => { setProblema(""); setConfirmando(true); }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-700">
              <Trash2 size={13} /> Borrar
            </button>
          )}
          {equipo && confirmando && (
            <span className="inline-flex items-center gap-2 text-xs">
              <span className="text-slate-600">¿Borrarla?</span>
              <button type="button" onClick={() => void confirmarBorrado()} disabled={borrando} className="font-semibold text-red-700 hover:underline disabled:opacity-60">
                {borrando ? "Borrando…" : "Sí, borrar"}
              </button>
              <button type="button" onClick={() => setConfirmando(false)} disabled={borrando} className="font-semibold text-slate-500 hover:underline">Cancelar</button>
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
